// engagement-components/SendTestModal.jsx
// "Send Test" modal — used in JourneyBuilder and CampaignForm
// Asks which channel to test, then asks for phone/email.
// Calls /feedback/test/send — falls back gracefully if API not ready.

import React, { useState } from "react";
import { Spinner } from "react-bootstrap";
import { FaWhatsapp, FaSms, FaEnvelope, FaCheck, FaTimes, FaPaperPlane } from "react-icons/fa";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";
import { useNotify } from "./shared";

const CHANNELS = [
  {
    key:   "whatsapp",
    label: "WhatsApp",
    icon:  <FaWhatsapp className="text-success" size={18} />,
    color: "#198754",
    fieldLabel: "WhatsApp phone number",
    placeholder: "+91 98765 43210",
    hint: "Include country code. Guest must have WhatsApp installed.",
    validate: (v) => /^\+?\d[\d\s-]{8,}$/.test(v.trim()),
    validationMsg: "Enter a valid phone number with country code",
  },
  {
    key:   "sms",
    label: "SMS",
    icon:  <FaSms className="text-secondary" size={18} />,
    color: "#6c757d",
    fieldLabel: "Mobile number (for SMS)",
    placeholder: "+91 98765 43210",
    hint: "The test SMS will be sent to this number.",
    validate: (v) => /^\+?\d[\d\s-]{8,}$/.test(v.trim()),
    validationMsg: "Enter a valid mobile number with country code",
  },
  {
    key:   "email",
    label: "Email",
    icon:  <FaEnvelope className="text-primary" size={18} />,
    color: "#0d6efd",
    fieldLabel: "Email address",
    placeholder: "you@example.com",
    hint: "The test email will be sent to this address.",
    validate: (v) => /\S+@\S+\.\S+/.test(v.trim()),
    validationMsg: "Enter a valid email address",
  },
];

const SendTestModal = ({
  show,
  onHide,
  // Context: campaign or journey being tested
  campaignName,
  journeyName,
  // Optional: pre-selected template content for preview
  templateContent,
  templateChannel,  // pre-select channel if template has a specific channel
  steps,            // for journeys — array of steps to pick from
}) => {
  const notify = useNotify();

  const [channel,   setChannel]   = useState(templateChannel?.toLowerCase() || "whatsapp");
  const [recipient, setRecipient] = useState("");
  const [stepIdx,   setStepIdx]   = useState(0);
  const [sending,   setSending]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [error,     setError]     = useState("");

  const selectedChannel = CHANNELS.find((c) => c.key === channel) || CHANNELS[0];
  const name = campaignName || journeyName || "this";

  const reset = () => {
    setChannel(templateChannel?.toLowerCase() || "whatsapp");
    setRecipient("");
    setStepIdx(0);
    setSending(false);
    setSent(false);
    setError("");
  };

  const handleClose = () => { reset(); onHide(); };

const sendTest = async () => {
  setError("");
  if (!recipient.trim()) { setError("Please enter a recipient."); return; }
  if (!selectedChannel.validate(recipient)) { setError(selectedChannel.validationMsg); return; }
 
  setSending(true);
  const payload = {
    channel,
    recipient:     recipient.trim(),
    campaign_name: campaignName,
    journey_name:  journeyName,
    step_index:    steps ? stepIdx : undefined,
    template:      templateContent,
  };
 
  try {
    const res = await axios.post(`${apiUrl}/feedback/test/send`, payload, authHeaders());
    // 2xx — real success
    if (res.data && res.data.ok) {
      setSent(true);
      notify("success", `Test ${channel} sent to ${recipient.trim()}`);
    } else {
      // server responded 2xx but ok:false — treat as failure
      setError(res.data?.error || "Send failed. Please try again.");
    }
  } catch (err) {
    // Distinguish a REAL delivery failure from a network/server error.
    const status = err?.response?.status;
    const apiMsg = err?.response?.data?.error;
 
    if (status === 422) {
      // Well-formed request, delivery failed — show the real reason.
      setError(apiMsg || "Message could not be delivered. Check your channel settings.");
    } else if (status === 400) {
      setError(apiMsg || "Invalid request. Check the channel and recipient.");
    } else if (status === 401 || status === 403) {
      setError("Your session expired. Please log in again.");
    } else {
      // Genuine network outage / 5xx — this is the ONLY case where a
      // soft message is appropriate, and even here we do NOT claim success.
      setError("Could not reach the server. Check your connection and try again.");
    }
  } finally {
    setSending(false);
  }
};
 


  return (
    <>
      {show && (
        <div style={{ position:"fixed", inset:0, zIndex:1070,
          display:"flex", alignItems:"stretch" }}>
          {/* Backdrop */}
          <div onClick={handleClose}
            style={{ flex:1, background:"rgba(0,0,0,0.4)", cursor:"pointer" }} />
          {/* Panel */}
          <div style={{ width:460, flexShrink:0, background:"#fff",
            display:"flex", flexDirection:"column", height:"100vh",
            boxShadow:"-8px 0 40px rgba(0,0,0,0.15)" }}>

            {/* Fixed header */}
            <div style={{ padding:"18px 24px", borderBottom:"1px solid #e5e7eb",
              display:"flex", alignItems:"center", justifyContent:"space-between",
              flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:9, background:"#eff6ff",
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <FaPaperPlane style={{ color:"#0d6efd", fontSize:"0.9rem" }} />
                </div>
                <div>
                  <div style={{ fontSize:"0.94rem", fontWeight:800, color:"#111827" }}>
                    Send Test Message
                  </div>
                  <div style={{ fontSize:"0.73rem", color:"#9ca3af" }}>
                    Testing: {name}
                  </div>
                </div>
              </div>
              <button onClick={handleClose}
                style={{ width:32, height:32, borderRadius:"50%", border:"1px solid #e5e7eb",
                  background:"#f9fafb", cursor:"pointer", display:"flex",
                  alignItems:"center", justifyContent:"center",
                  fontSize:"1.1rem", color:"#6b7280" }}>
                ×
              </button>
            </div>

            {/* Scrollable body */}
            <div style={{ flex:1, overflowY:"auto", padding:"20px 24px" }}>
              {sent ? (
                <div style={{ textAlign:"center", padding:"48px 0" }}>
                  <div style={{ width:64, height:64, borderRadius:"50%",
                    background:"#f0fdf4", display:"flex",
                    alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
                    <FaCheck size={26} style={{ color:"#059669" }} />
                  </div>
                  <div style={{ fontSize:"1.1rem", fontWeight:800, color:"#111827",
                    marginBottom:8 }}>
                    Test Sent!
                  </div>
                  <p style={{ fontSize:"0.84rem", color:"#6b7280", marginBottom:6 }}>
                    {channel === "whatsapp" && <><FaWhatsapp style={{ color:"#25d366", marginRight:4 }} />WhatsApp</>}
                    {channel === "sms"      && <><FaSms style={{ color:"#6c757d", marginRight:4 }} />SMS</>}
                    {channel === "email"    && <><FaEnvelope style={{ color:"#0d6efd", marginRight:4 }} />Email</>}
                    {" "}sent to <strong>{recipient}</strong>
                  </p>
                  <p style={{ fontSize:"0.78rem", color:"#9ca3af", marginBottom:24 }}>
                    Check your {channel === "email" ? "inbox (and spam)" : "device"}.
                  </p>
                  <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
                    <button onClick={reset}
                      style={{ padding:"8px 18px", borderRadius:8,
                        border:"1px solid #e5e7eb", background:"#fff",
                        color:"#374151", fontSize:"0.82rem", fontWeight:600,
                        cursor:"pointer" }}>
                      Test another
                    </button>
                    <button onClick={handleClose}
                      style={{ padding:"8px 18px", borderRadius:8, border:"none",
                        background:"#6b7280", color:"#fff", fontSize:"0.82rem",
                        fontWeight:600, cursor:"pointer" }}>
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Context banner */}
                  <div style={{ padding:"12px 14px", borderRadius:10,
                    background:"#f0f4ff", border:"1px solid #c7d2fe",
                    fontSize:"0.78rem", color:"#374151", marginBottom:20 }}>
                    A <strong>real message</strong> will be sent to the number/email below
                    using your live WhatsApp/SMTP credentials. It will be labelled as a test.
                  </div>

                  {/* Journey step picker */}
                  {steps && steps.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                      <div style={{ fontSize:"0.72rem", fontWeight:800, color:"#9ca3af",
                        textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
                        Which step to test?
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        {steps.map((step, i) => (
                          <label key={step.id}
                            style={{ display:"flex", alignItems:"center", gap:12,
                              padding:"10px 14px", borderRadius:9, cursor:"pointer",
                              border:`1.5px solid ${stepIdx===i ? "#6366f1" : "#e5e7eb"}`,
                              background: stepIdx===i ? "#eef2ff" : "#fff" }}>
                            <input type="radio" checked={stepIdx===i}
                              onChange={()=>{ setStepIdx(i); setChannel(step.channel?.toLowerCase()||"whatsapp"); }} />
                            <div>
                              <div style={{ fontSize:"0.82rem", fontWeight:700, color:"#111827" }}>
                                Step {i+1} · {step.channel}
                              </div>
                              <div style={{ fontSize:"0.72rem", color:"#9ca3af" }}>
                                {step.offsetDays===0 ? "Same day"
                                  : `${Math.abs(step.offsetDays)}d ${step.offsetDays<0?"before":"after"}`}
                                {" at "}{step.executionTime}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Channel buttons */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:"0.72rem", fontWeight:800, color:"#9ca3af",
                      textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
                      Send via
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      {CHANNELS.map(ch => (
                        <button key={ch.key}
                          onClick={()=>{ setChannel(ch.key); setError(""); }}
                          style={{ flex:1, padding:"9px 0", borderRadius:9,
                            border:`1.5px solid ${channel===ch.key ? ch.color : "#e5e7eb"}`,
                            background: channel===ch.key ? ch.color+"15" : "#fff",
                            color: channel===ch.key ? ch.color : "#6b7280",
                            fontWeight:700, fontSize:"0.8rem", cursor:"pointer",
                            display:"flex", alignItems:"center",
                            justifyContent:"center", gap:6 }}>
                          {ch.icon} {ch.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recipient */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:"0.72rem", fontWeight:800, color:"#9ca3af",
                      textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
                      {selectedChannel.fieldLabel}
                    </div>
                    <div style={{ display:"flex", overflow:"hidden", borderRadius:9,
                      border:`1.5px solid ${selectedChannel.color}` }}>
                      <div style={{ padding:"0 12px", background:selectedChannel.color,
                        display:"flex", alignItems:"center" }}>
                        {selectedChannel.icon}
                      </div>
                      <input
                        type={channel==="email" ? "email" : "tel"}
                        value={recipient}
                        onChange={e=>{ setRecipient(e.target.value); setError(""); }}
                        placeholder={selectedChannel.placeholder}
                        onKeyDown={e=>e.key==="Enter" && sendTest()}
                        autoFocus
                        style={{ flex:1, padding:"10px 14px", border:"none",
                          outline:"none", fontSize:"0.84rem", color:"#111827" }} />
                    </div>
                    <div style={{ fontSize:"0.72rem", color:"#9ca3af", marginTop:5 }}>
                      {selectedChannel.hint}
                    </div>
                  </div>

                  {error && (
                    <div style={{ padding:"10px 14px", borderRadius:8,
                      background:"#fef2f2", border:"1px solid #fecaca",
                      color:"#dc2626", fontSize:"0.8rem", marginBottom:16,
                      display:"flex", alignItems:"center", gap:8 }}>
                      <FaTimes /> {error}
                    </div>
                  )}

                  {/* Message preview */}
                  {templateContent && (
                    <div style={{ marginBottom:20 }}>
                      <div style={{ fontSize:"0.72rem", fontWeight:800, color:"#9ca3af",
                        textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
                        Message preview
                      </div>
                      {channel === "whatsapp" ? (
                        <div style={{ padding:12, borderRadius:10, background:"#e5ddd5" }}>
                          <div style={{ marginLeft:"auto", padding:"10px 12px",
                            borderRadius:"12px 2px 12px 12px", background:"#dcf8c6",
                            maxWidth:"92%", fontSize:"0.82rem", lineHeight:1.6,
                            whiteSpace:"pre-wrap" }}>
                            {templateContent}
                            <div style={{ textAlign:"right", fontSize:"0.65rem",
                              color:"#9ca3af", marginTop:4 }}>✓✓ Test</div>
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding:12, borderRadius:10, border:"1px solid #e5e7eb",
                          background:"#f8f9fa", fontSize:"0.82rem",
                          lineHeight:1.6, whiteSpace:"pre-wrap" }}>
                          {templateContent}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Fixed footer */}
            {!sent && (
              <div style={{ padding:"16px 24px", borderTop:"1px solid #e5e7eb",
                flexShrink:0, background:"#fff",
                display:"flex", gap:10 }}>
                <button onClick={handleClose}
                  style={{ flex:1, padding:"10px 0", borderRadius:9,
                    border:"1px solid #e5e7eb", background:"#fff",
                    color:"#374151", fontSize:"0.84rem", fontWeight:600,
                    cursor:"pointer" }}>
                  Cancel
                </button>
                <button onClick={sendTest}
                  disabled={sending || !recipient.trim()}
                  style={{ flex:2, padding:"10px 0", borderRadius:9, border:"none",
                    background: sending || !recipient.trim() ? "#a5b4fc" : "#0d6efd",
                    color:"#fff", fontSize:"0.84rem", fontWeight:700,
                    cursor: sending || !recipient.trim() ? "not-allowed" : "pointer",
                    display:"flex", alignItems:"center",
                    justifyContent:"center", gap:8 }}>
                  {sending
                    ? <><Spinner animation="border" size="sm" /> Sending…</>
                    : <><FaPaperPlane size={13} /> Send Test {selectedChannel.label}</>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

};
export default SendTestModal;
